import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useCloud } from '../context/CloudContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import { toId } from '../lib/ids';

const CONTRACT_TYPES = ['Credit', 'Commitment', 'PayAsYouGo', 'Subscription'];

export default function ContractNew() {
  const navigate = useNavigate();
  const { providers } = useCloud();
  const groupsReq = useAsync(() => api.getUserGroups(), []);
  const groups = groupsReq.data || [];

  const [form, setForm] = useState({
    providerId: '',
    type: 'Credit',
    start: '',
    end: '',
    value: '',
    currency: 'USD',
    groupId: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const providerId = form.providerId || providers[0]?.id || '';
    if (!providerId) {
      setError('Select a provider');
      return;
    }
    if (!form.start || !form.end) {
      setError('Start and end dates are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.createContract({
        providerId,
        type: form.type,
        start: form.start,
        end: form.end,
        value: parseFloat(form.value) || 0,
        currency: form.currency || 'USD',
        groupId: toId(form.groupId),
      });
      navigate(`/contracts/${created.id}`);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="stack">
      <PageHeader
        title="Create contract"
        subtitle="Assign a provider under commercial terms"
      />

      <Card title="Contract details">
        <form className="form" onSubmit={submit}>
          <div className="form-grid">
            <label className="field">
              <span>Provider</span>
              <select
                className="input"
                value={form.providerId || providers[0]?.id || ''}
                onChange={update('providerId')}
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Contract type</span>
              <select className="input" value={form.type} onChange={update('type')}>
                {CONTRACT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Start date</span>
              <input
                className="input"
                type="date"
                value={form.start}
                onChange={update('start')}
                required
              />
            </label>
            <label className="field">
              <span>End date</span>
              <input
                className="input"
                type="date"
                value={form.end}
                onChange={update('end')}
                required
              />
            </label>
            <label className="field">
              <span>Value</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 50000"
                value={form.value}
                onChange={update('value')}
              />
            </label>
            <label className="field">
              <span>Currency</span>
              <input
                className="input"
                value={form.currency}
                onChange={update('currency')}
              />
            </label>
          </div>

          <label className="field">
            <span>User group (optional — can associate later)</span>
            <select className="input" value={form.groupId} onChange={update('groupId')}>
              <option value="">Unassigned</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.tenancyName ? ` · ${g.tenancyName}` : ''}
                </option>
              ))}
            </select>
          </label>

          {error ? <div className="form-error">{error}</div> : null}

          <div className="modal__footer">
            <Link className="btn btn--ghost" to="/contracts">
              Cancel
            </Link>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create contract'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
